import React, { useState } from 'react';
import * as XLSX from 'xlsx'; 
import { db } from '../../../../firebase/config';
import { collection, getDocs, collectionGroup, addDoc } from 'firebase/firestore';
// Importamos los mismos helpers robustos para mantener la consistencia entre ramos
import { formatCurrency, parseImporteExcel, normalizePoliza, normalizeText, normalizePeriodo } from './helpers';

/**
 * ImportadorTab - Especializado en liquidaciones de Pólizas Patrimoniales.
 * Utiliza un cruce masivo por nPoliza y asigna el tipoRamo: 'PATRIMONIALES'.
 */
const ImportadorTab = ({ maestros, setActiveTab }) => {
    const [loading, setLoading] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [stats, setStats] = useState({ total: 0, ok: 0, error: 0 });
    const [fileName, setFileName] = useState('');

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFileName(file.name);
            setPreviewData([]);
            setStats({ total: 0, ok: 0, error: 0 });
            window.tempSelectedFile = file; 
        }
    };

    const handleProcessFile = async () => {
        const file = window.tempSelectedFile;
        if (!file) return alert("Selecciona un archivo.");
        setLoading(true);

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 }); 
                await processExcelRows(data);
            } catch (error) {
                console.error("Error procesando Excel:", error);
                alert("Error al leer el archivo. Verifica el formato.");
                setLoading(false);
            }
        };
        reader.readAsBinaryString(file);
    };

    const processExcelRows = async (rows) => {
        if (rows.length < 2) { 
            setLoading(false); 
            return alert("El archivo está vacío."); 
        }

        // 1. Identificación flexible de columnas
        let headerRowIndex = -1;
        for (let i = 0; i < Math.min(rows.length, 20); i++) {
            const rowStr = rows[i].map(c => normalizeText(c));
            if ((rowStr.some(h => h.includes('poliza') || h.includes('nro'))) && 
               (rowStr.some(h => h.includes('comision') || h.includes('prima') || h.includes('importe')))) {
                headerRowIndex = i;
                break;
            }
        }

        if (headerRowIndex === -1) { 
            setLoading(false); 
            return alert("No se encontraron columnas necesarias: Póliza e Importes."); 
        }

        const header = rows[headerRowIndex].map(h => String(h).toLowerCase());
        const idxPeriodo = header.findIndex(h => h.includes('periodo') || h.includes('fecha'));
        const idxPoliza = header.findIndex(h => h.includes('poliza') || h.includes('nro'));
        const idxComision = header.findIndex(h => h.includes('comision') || h.includes('importe'));
        const idxPrima = header.findIndex(h => h.includes('prima') || h.includes('premio'));

        // 2. Mapeo Maestro de Pólizas
        const polizasRef = collectionGroup(db, 'polizas');
        const snapshot = await getDocs(polizasRef);
        const polizasMap = new Map();
        
        snapshot.forEach(docSnap => {
            const data = docSnap.data();
            if (data.nPoliza) {
                const normalizedNum = normalizePoliza(data.nPoliza);
                polizasMap.set(normalizedNum, { 
                    id: docSnap.id, 
                    clienteId: docSnap.ref.path.split('/')[3],
                    ...data 
                });
            }
        });

        const processed = [];
        let countOk = 0;
        let countError = 0;

        // 3. Iteración de filas
        for (let i = headerRowIndex + 1; i < rows.length; i++) {
            const row = rows[i];
            if (!row[idxPoliza]) continue; 

            const nPolizaExcel = String(row[idxPoliza]).trim(); 
            const nPolizaNorm = normalizePoliza(nPolizaExcel);
            
            const valComision = idxComision !== -1 ? parseImporteExcel(row[idxComision]) : 0;
            const valPrima = idxPrima !== -1 ? parseImporteExcel(row[idxPrima]) : 0;
            
            // Forzamos el estándar 01/MM/YYYY
            const periodoStd = normalizePeriodo(idxPeriodo !== -1 ? row[idxPeriodo] : '');

            const match = polizasMap.get(nPolizaNorm);
            let status = 'ERROR';
            let message = 'Póliza no registrada';
            let dataToSave = null;

            if (match) {
                if (valComision !== 0 || valPrima !== 0) {
                    status = 'OK';
                    message = 'Registro listo';
                    countOk++;

                    const canalObj = maestros.mapaCanales[match.subcanalId] || maestros.mapaCanales[match.canal]; 
                    const formaLiq = canalObj?.formaLiquidarPatrimoniales || 'Comisión';
                    
                    dataToSave = {
                        polizaId: match.id,
                        clienteId: match.clienteId,
                        clienteNombre: match.clienteNombreCompleto || 'S/D',
                        nPoliza: match.nPoliza,
                        compania: match.compania, 
                        riesgo: match.riesgo,
                        canal: match.canal || '', 
                        subcanalId: match.subcanalId || '', 
                        vendedorAsociadoId: match.vendedorAsociadoId || '', 
                        importeComision: valComision,
                        importePrima: valPrima,
                        formaLiquidacion: formaLiq,
                        tipoRamo: 'PATRIMONIALES' // Unificado para reportes
                    };
                } else {
                    message = 'Importes en cero';
                    countError++;
                }
            } else {
                countError++;
            }

            processed.push({
                id: i,
                periodo: periodoStd,
                nPoliza_Excel: nPolizaExcel, 
                valComision,
                valPrima,
                status,
                message,
                dataToSave
            });
        }

        setPreviewData(processed);
        setStats({ total: processed.length, ok: countOk, error: countError });
        setLoading(false);
    };

    const handleConfirmImport = async () => {
        const toImport = previewData.filter(i => i.status === 'OK');
        if (toImport.length === 0) return;
        setLoading(true);

        try {
            const batchId = new Date().getTime().toString();
            
            // Registro de Remesa
            await addDoc(collection(db, 'public/data/remesas'), {
                id: batchId,
                fecha: new Date().toISOString(),
                nombreArchivo: fileName,
                registros: toImport.length,
                periodo: toImport[0].periodo,
                tipo: 'PATRIMONIALES'
            });

            // Liquidaciones individuales
            for (const item of toImport) {
                await addDoc(collection(db, 'public/data/liquidaciones'), {
                    periodo: item.periodo,
                    nPoliza: item.dataToSave.nPoliza,
                    fechaImportacion: new Date().toISOString(),
                    batchId: batchId,
                    ...item.dataToSave 
                });
            }

            alert(`Importación finalizada: ${toImport.length} registros cargados.`);
            setPreviewData([]);
            setFileName('');
            window.tempSelectedFile = null;
            setActiveTab('remesas');

        } catch (e) { 
            console.error(e); 
            alert("Error al guardar en base de datos."); 
        } finally { 
            setLoading(false); 
        }
    };

    return (
        <div className="bg-white p-8 rounded-[2rem] shadow-xl border border-gray-100 animate-fade-in">
            <div className="mb-10 border-b border-gray-100 pb-10">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter">Importador de Patrimoniales</h3>
                        <p className="text-xs text-slate-400 font-bold uppercase mt-1 tracking-widest italic">Cruce masivo por número de póliza</p>
                    </div>
                    {fileName && (
                        <div className="bg-cyan-50 px-4 py-2 rounded-2xl border border-cyan-100 shadow-sm flex items-center gap-2">
                             <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                             <span className="text-[10px] font-black text-cyan-700 uppercase tracking-widest">{fileName}</span>
                        </div>
                    )}
                </div>
                
                <div className="flex flex-col gap-6 items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-slate-200 border-dashed rounded-[2.5rem] cursor-pointer bg-slate-50/50 hover:bg-cyan-50 hover:border-cyan-300 transition-all group">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <div className="bg-white p-4 rounded-3xl shadow-md mb-4 group-hover:scale-110 transition-transform duration-300">
                                <svg className="w-10 h-10 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                            </div>
                            <p className="mb-2 text-base text-slate-600 font-bold uppercase tracking-tighter">Suelte el archivo aquí o <span className="text-cyan-600">busque en su PC</span></p>
                            <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em]">Formato admitido: XLSX, XLS, CSV</p>
                        </div>
                        <input type="file" className="hidden" accept=".xlsx, .xls, .csv" onChange={handleFileSelect} />
                    </label>

                    <button 
                        onClick={handleProcessFile} 
                        disabled={!fileName || loading} 
                        className={`px-12 py-4 rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-2xl transition-all transform active:scale-95 ${!fileName || loading ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none' : 'bg-slate-900 text-white hover:bg-slate-800 shadow-slate-200'}`}
                    >
                        {loading ? 'Analizando...' : 'Iniciar Análisis de Pólizas'}
                    </button>
                </div>
            </div>

            {!loading && previewData.length > 0 && (
                <div className="animate-fade-in">
                    <div className="flex justify-between items-center mb-6 bg-slate-900 p-5 rounded-[1.5rem] shadow-xl">
                        <h3 className="text-xs font-black text-white uppercase tracking-[0.2em]">Vista Previa del Lote</h3>
                        <div className="flex gap-4">
                            <div className="bg-white/10 px-4 py-2 rounded-xl text-center"><p className="text-[9px] font-black text-slate-400 uppercase">Procesados</p><p className="text-lg font-black text-white mt-1 leading-none">{stats.total}</p></div>
                            <div className="bg-emerald-500/20 px-4 py-2 rounded-xl text-center"><p className="text-[9px] font-black text-emerald-400 uppercase">Correctos</p><p className="text-lg font-black text-emerald-400 mt-1 leading-none">{stats.ok}</p></div>
                            <div className="bg-rose-500/20 px-4 py-2 rounded-xl text-center"><p className="text-[9px] font-black text-rose-400 uppercase">Errores</p><p className="text-lg font-black text-rose-400 mt-1 leading-none">{stats.error}</p></div>
                        </div>
                    </div>

                    <div className="overflow-x-auto border border-slate-100 rounded-[1.5rem] mb-8 shadow-inner bg-slate-50/50 max-h-[500px] custom-scrollbar">
                        <table className="min-w-full text-left border-separate border-spacing-0">
                            <thead className="sticky top-0 bg-white shadow-sm z-10">
                                <tr className="text-slate-400 font-black uppercase text-[10px] tracking-widest">
                                    <th className="px-8 py-5 border-b border-slate-100">Póliza Excel</th>
                                    <th className="px-8 py-5 border-b border-slate-100">Periodo</th>
                                    <th className="px-8 py-5 border-b border-slate-100 text-right">Comisión $</th>
                                    <th className="px-8 py-5 border-b border-slate-100 text-right">Prima $</th>
                                    <th className="px-8 py-5 border-b border-slate-100 text-center">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {previewData.map((r, i) => (
                                    <tr key={i} className={`group transition-all ${r.status === 'OK' ? 'hover:bg-emerald-50/50' : 'bg-rose-50/30 hover:bg-rose-50'}`}>
                                        <td className="px-8 py-4 font-mono font-black text-slate-600 border-b border-slate-50 text-sm">{r.nPoliza_Excel}</td>
                                        <td className="px-8 py-4 text-slate-500 font-bold border-b border-slate-50">{r.periodo}</td>
                                        <td className="px-8 py-4 font-black text-slate-700 border-b border-slate-50 text-right">{formatCurrency(r.valComision)}</td>
                                        <td className="px-8 py-4 font-black text-slate-700 border-b border-slate-50 text-right">{formatCurrency(r.valPrima)}</td>
                                        <td className="px-8 py-4 border-b border-slate-50 text-center">
                                            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-tighter border ${r.status === 'OK' ? 'text-emerald-600 bg-emerald-50 border-emerald-100' : 'text-rose-600 bg-rose-50 border-rose-100'}`}>
                                                {r.status === 'OK' ? '✓' : '✕'} {r.message}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-end gap-6 p-4">
                        <button 
                            onClick={() => { setPreviewData([]); setFileName(''); window.tempSelectedFile = null; }} 
                            className="text-slate-400 hover:text-slate-600 font-black uppercase text-[11px] tracking-widest transition-colors"
                        >
                            Descartar lote
                        </button>
                        <button 
                            onClick={handleConfirmImport} 
                            disabled={stats.ok === 0} 
                            className="px-12 py-3.5 rounded-2xl bg-cyan-600 text-white font-black uppercase text-[11px] tracking-[0.2em] shadow-xl shadow-cyan-200 hover:bg-cyan-700 transition-all transform active:scale-95 disabled:grayscale disabled:shadow-none"
                        >
                            Confirmar Importación
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportadorTab;